import React, { useState, useEffect } from 'react';
import { Users, Wrench, RefreshCw, Filter, CheckCircle, Clock, Award } from 'lucide-react';
import api, { API_URL } from '../../services/api';

const RapportOuvriers = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const [dateDebut, setDateDebut] = useState(today.slice(0, 8) + '01');
  const [dateFin, setDateFin] = useState(today);
  const [showFilters, setShowFilters] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      const r = await api.get(`${API_URL}/rapports/ouvriers?${params.toString()}`);
      setData(r.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;

  const ouvriers = data?.ouvriers || [];
  const etapes = data?.etapes || [];
  const totalEtapes = ouvriers.reduce((s, o) => s + (o.etapes_terminees || 0), 0);

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Rapport Ouvriers</h1>
              <p className="text-teal-200 text-sm">Productivite et performance des ouvriers</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg">
                <Filter className="h-4 w-4" /><span>Periode</span>
              </button>
              <button onClick={loadData} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg"><RefreshCw className="h-4 w-4" /></button>
            </div>
          </div>
          {showFilters && (
            <div className="mt-4 bg-white/10 rounded-xl p-4 flex flex-col sm:flex-row items-end gap-3">
              <div><label className="block text-xs text-teal-200 mb-1">Du</label><input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
              <div><label className="block text-xs text-teal-200 mb-1">Au</label><input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" /></div>
              <button onClick={loadData} className="px-6 py-2 bg-white text-teal-700 font-medium rounded-lg">Appliquer</button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Ouvriers actifs</p>
            <p className="text-2xl font-black text-gray-900">{ouvriers.filter(o => o.etapes_terminees > 0).length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Etapes terminees</p>
            <p className="text-2xl font-black text-green-700">{totalEtapes}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">En cours</p>
            <p className="text-2xl font-black text-orange-600">{ouvriers.reduce((s, o) => s + (o.etapes_en_cours || 0), 0)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Ordres travailles</p>
            <p className="text-2xl font-black text-indigo-700">{ouvriers.reduce((s, o) => s + (o.nb_ordres || 0), 0)}</p>
          </div>
        </div>

        {/* Classement ouvriers */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b">
            <h2 className="font-bold text-gray-800 flex items-center gap-2"><Users className="h-5 w-5 text-teal-600" />CLASSEMENT DES OUVRIERS</h2>
          </div>

          {/* Mobile */}
          <div className="md:hidden divide-y">
            {ouvriers.map((o, i) => {
              const pct = totalEtapes > 0 ? ((o.etapes_terminees || 0) / totalEtapes * 100).toFixed(1) : 0;
              return (
                <div key={o.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-50 text-gray-400'}`}>{i+1}</span>
                    <div>
                      <p className="font-bold text-gray-900">{o.nom}</p>
                      <p className="text-xs text-gray-500">{o.specialite || '-'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-teal-700">{o.etapes_terminees || 0} <span className="text-xs text-gray-400">etapes</span></p>
                    <p className="text-xs text-gray-500">{o.nb_ordres || 0} OFs - {pct}%</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Ouvrier</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Specialite</th>
                  <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Ordres</th>
                  <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Etapes terminees</th>
                  <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">En cours</th>
                  <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Part</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Progression</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ouvriers.map((o, i) => {
                  const pct = totalEtapes > 0 ? ((o.etapes_terminees || 0) / totalEtapes * 100) : 0;
                  return (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {i < 3 ? <Award className={`h-5 w-5 ${i === 0 ? 'text-yellow-500' : i === 1 ? 'text-gray-400' : 'text-orange-500'}`} /> : <span className="text-gray-400">{i+1}</span>}
                      </td>
                      <td className="px-4 py-3 font-bold text-gray-900">{o.nom}</td>
                      <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-teal-100 text-teal-700">{o.specialite || '-'}</span></td>
                      <td className="px-4 py-3 text-center font-medium">{o.nb_ordres || 0}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-green-700 font-bold"><CheckCircle className="h-3 w-3" />{o.etapes_terminees || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(o.etapes_en_cours || 0) > 0 ? <span className="inline-flex items-center gap-1 text-orange-600 font-medium"><Clock className="h-3 w-3" />{o.etapes_en_cours}</span> : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-600">{pct.toFixed(1)}%</td>
                      <td className="px-4 py-3 w-32">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Etapes par type */}
        {etapes.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="px-5 py-4 border-b">
              <h2 className="font-bold text-gray-800 flex items-center gap-2"><Wrench className="h-5 w-5 text-teal-600" />ETAPES PAR TYPE</h2>
            </div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {etapes.map(e => (
                <div key={e.nom_etape} className="bg-gray-50 rounded-lg p-3 border">
                  <p className="text-xs font-medium text-gray-600 truncate">{e.nom_etape}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-lg font-black text-gray-900">{e.nb}</span>
                    <span className="text-xs text-green-600">{e.terminees} ok</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RapportOuvriers;
